import { config, getBackendBaseUrl } from './environment';

function tryParseJson(s: string): unknown {
  try {
    return JSON.parse(s);
  } catch {
    return null;
  }
}

function pickId(d: Record<string, unknown>, ...keys: string[]): string {
  for (const k of keys) {
    const v = d[k];
    if (v !== undefined && v !== null && String(v).trim()) return String(v).trim();
  }
  return '';
}

/**
 * Origem do postMessage (alinhado à doc Meta: event.origin.endsWith('facebook.com')).
 * Usamos hostname para evitar correspondências ambíguas no path da URL.
 */
export function isMetaEmbeddedSignupMessageOrigin(origin: string): boolean {
  try {
    const { hostname } = new URL(origin);
    if (hostname === 'facebook.com' || hostname.endsWith('.facebook.com')) return true;
    if (hostname === 'facebook.net' || hostname.endsWith('.facebook.net')) return true;
    if (hostname === 'fb.com' || hostname.endsWith('.fb.com')) return true;
    return false;
  } catch {
    return typeof origin === 'string' && origin.toLowerCase().endsWith('facebook.com');
  }
}

/** Eventos de sucesso na doc Meta (Embedded Signup). */
function isWaEmbeddedSuccessEvent(ev: unknown): boolean {
  if (typeof ev !== 'string' || !ev) return false;
  if (ev === 'CANCEL' || ev === 'ERROR') return false;
  return ev.startsWith('FINISH');
}

export interface WaEmbeddedSignupFinishPayload {
  waba_id: string;
  /** Vazio em FINISH_ONLY_WABA até o backend listar números na WABA. */
  phone_number_id: string;
  business_id?: string;
  /** Valor bruto de `event` (ex.: FINISH_WHATSAPP_BUSINESS_APP_ONBOARDING). */
  metaEvent?: string;
}

/**
 * Interpreta `message.data` do WA_EMBEDDED_SIGNUP.
 * `event.data` pode vir string JSON (doc Meta usa JSON.parse).
 *
 * O redirect para app.pooh.pro/facebook/callback é separado: o postMessage deve chegar à **janela que abriu o popup**
 * (ex.: modelo.pooh.pro). Essa origem tem de constar nos Domínios permitidos da app na Meta.
 */
export function parseWaEmbeddedSignupFinishPayload(raw: unknown): WaEmbeddedSignupFinishPayload | null {
  const data = typeof raw === 'string' ? tryParseJson(raw) : raw;
  if (!data || typeof data !== 'object') return null;
  const o = data as Record<string, unknown>;
  if (o.type !== 'WA_EMBEDDED_SIGNUP') return null;
  const ev = o.event;
  if (!isWaEmbeddedSuccessEvent(ev)) return null;
  const inner = o.data;
  if (!inner || typeof inner !== 'object') return null;
  const d = inner as Record<string, unknown>;
  const waba_id = pickId(d, 'waba_id', 'wabaId');
  const phone_number_id = pickId(d, 'phone_number_id', 'phoneNumberId');
  const business_id = pickId(d, 'business_id', 'businessId');

  if (!waba_id) return null;

  const metaEvent = typeof ev === 'string' ? ev : undefined;
  if (metaEvent === 'FINISH_ONLY_WABA') {
    return { waba_id, phone_number_id: phone_number_id || '', business_id: business_id || undefined, metaEvent };
  }
  if (!phone_number_id) return null;

  return { waba_id, phone_number_id, business_id: business_id || undefined, metaEvent };
}

/** Monta URL absoluta do POST /complete a partir do slug do tenant (state OAuth). */
export function buildEmbeddedSignupCompleteUrl(tenantSlug: string): string | null {
  const slug = tenantSlug.trim();
  if (!slug) return null;

  const template = config.embeddedSignupCompleteUrlTemplate.trim();
  if (template) {
    return template.replace(/\{tenant\}/g, slug);
  }

  if (process.env.NODE_ENV === 'development') {
    const base = getBackendBaseUrl();
    if (base) {
      const clean = base.replace(/\/+$/, '');
      return `${clean}/api/v1/whatsapp-cloud-api/embedded-signup/complete`;
    }
  }

  return null;
}

/**
 * Deriva a URL pública de /finish-relay no API do tenant a partir do template de /complete.
 * Mantém o mesmo padrão {tenant} e troca o sufixo do path.
 */
export function buildEmbeddedSignupFinishRelayUrl(tenantSlug: string): string | null {
  const complete = buildEmbeddedSignupCompleteUrl(tenantSlug);
  if (!complete) return null;
  return complete.replace(/\/complete(\/?$|\?)/, '/finish-relay$1');
}

/** Extrai o slug do tenant do state OAuth (`slug:nonce`). */
export function extractTenantFromState(state: string): string {
  if (!state) return '';
  const idx = state.indexOf(':');
  if (idx <= 0) return '';
  const slug = state.slice(0, idx).trim().toLowerCase();
  if (!/^[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?$/.test(slug)) return '';
  return slug;
}

/**
 * Hosts permitidos para o `apiFinishUrl` recebido em URL params no launcher.
 * Evita que um link malicioso force um POST para domínios arbitrários.
 */
export function isAllowedTenantApiHost(hostname: string): boolean {
  const h = (hostname || '').toLowerCase();
  if (!h) return false;
  return (
    /^api\.[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.pooh\.pro$/.test(h) ||
    /^api\.[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.poohapp\.com\.br$/.test(h) ||
    h === 'localhost' ||
    h === '127.0.0.1'
  );
}

/**
 * Hosts permitidos para o `returnUrl` (painel do tenant) após o launcher concluir.
 */
export function isAllowedTenantReturnHost(hostname: string): boolean {
  const h = (hostname || '').toLowerCase();
  if (!h) return false;
  return (
    /^[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.pooh\.pro$/.test(h) ||
    /^[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.poohapp\.com\.br$/.test(h) ||
    h === 'localhost' ||
    h === '127.0.0.1'
  );
}
